import { useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { brl, dt } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Landmark, Printer, CheckCircle2, Image as ImageIcon } from "lucide-react";
import html2canvas from "html2canvas";
import { toast } from "sonner";

export default function Proposal() {
  const [params] = useSearchParams();
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const data = useMemo(() => {
    const valor = parseFloat(params.get("valor") || "0");
    const taxa = parseFloat(params.get("taxa") || "0");
    const juros = Math.round(valor * (taxa / 100) * 100) / 100;
    const quitar = Math.round((valor + juros) * 100) / 100;
    return {
      cliente: params.get("cliente") || "",
      empresa: params.get("empresa") || "MS Soluções Financeiras",
      valor,
      taxa,
      juros,
      quitar,
      data: params.get("data") || "",
    };
  }, [params]);

  const downloadImage = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
      });
      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `proposta-${(data.cliente || "emprestimo").replace(/\s+/g, "_")}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success("Imagem da proposta salva");
      }, "image/png");
    } catch (e) {
      toast.error("Falha ao gerar imagem");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen py-10 px-4 flex justify-center print:bg-white print:py-0"
      data-testid="proposal-page"
    >
      <div className="w-full max-w-2xl">
        <div className="flex flex-wrap justify-end gap-2 mb-4 print:hidden">
          <Button
            variant="outline"
            className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
            onClick={downloadImage}
            disabled={busy}
            data-testid="proposal-download-image"
          >
            <ImageIcon className="w-4 h-4 mr-2" /> Baixar imagem
          </Button>
          <Button
            className="bg-blue-600 hover:bg-blue-500 text-white"
            onClick={() => window.print()}
            data-testid="proposal-print"
          >
            <Printer className="w-4 h-4 mr-2" /> Baixar PDF
          </Button>
        </div>

        <div
          ref={cardRef}
          className="bg-white text-slate-900 rounded-2xl shadow-2xl p-6 sm:p-8 md:p-10 print:shadow-none print:rounded-none"
          style={{ fontFamily: "Manrope, system-ui, sans-serif" }}
        >
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-6 mb-6">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center shrink-0">
                <Landmark className="w-6 h-6 text-white" />
              </div>
              <div className="min-w-0">
                <div
                  className="text-lg font-extrabold truncate"
                  style={{ fontFamily: "Outfit", letterSpacing: "-0.03em" }}
                >
                  {data.empresa}
                </div>
                <div className="text-xs text-slate-500 uppercase tracking-widest font-semibold">
                  Proposta de Empréstimo
                </div>
              </div>
            </div>
            {data.data && (
              <div className="text-right">
                <div className="text-[10px] text-slate-500 uppercase font-semibold">
                  Primeira parcela
                </div>
                <div className="text-sm font-mono text-slate-800 font-bold">{dt(data.data)}</div>
              </div>
            )}
          </div>

          {data.cliente && (
            <div className="mb-6">
              <div className="text-[11px] text-slate-500 uppercase tracking-widest font-semibold">
                Cliente
              </div>
              <div className="text-lg font-extrabold break-words">{data.cliente}</div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div className="rounded-lg border border-slate-200 p-4 min-w-0">
              <div className="text-[11px] text-slate-500 uppercase tracking-widest font-semibold">
                Valor emprestado
              </div>
              <div
                className="font-mono font-extrabold text-slate-900 break-words"
                style={{
                  fontSize: "clamp(1.25rem, 5vw, 1.75rem)",
                  letterSpacing: "-0.03em",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {brl(data.valor)}
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 p-4 min-w-0">
              <div className="text-[11px] text-slate-500 uppercase tracking-widest font-semibold">
                Taxa mensal
              </div>
              <div
                className="font-mono font-extrabold text-blue-700"
                style={{
                  fontSize: "clamp(1.25rem, 5vw, 1.75rem)",
                  letterSpacing: "-0.03em",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {data.taxa}%
              </div>
            </div>
          </div>

          <div className="rounded-xl border-2 border-blue-200 bg-blue-50 p-5 mb-4">
            <div className="text-xs uppercase tracking-widest text-blue-700 font-bold mb-3">
              Você escolhe como pagar
            </div>
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-blue-200 pb-3">
                <div className="min-w-0">
                  <div className="text-sm font-bold text-slate-900">Pagar só os juros</div>
                  <div className="text-xs text-slate-600">
                    A dívida principal continua e o vencimento vai para o próximo mês
                  </div>
                </div>
                <div
                  className="font-mono font-extrabold text-amber-600 break-words"
                  style={{
                    fontSize: "clamp(1rem, 4.5vw, 1.375rem)",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {brl(data.juros)}
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-bold text-slate-900">Quitar em 1 mês</div>
                  <div className="text-xs text-slate-600">Encerra o contrato integralmente</div>
                </div>
                <div
                  className="font-mono font-extrabold text-emerald-600 break-words"
                  style={{
                    fontSize: "clamp(1rem, 4.5vw, 1.375rem)",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {brl(data.quitar)}
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 text-xs text-slate-600 space-y-1 font-medium">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
              Você pode pagar qualquer valor entre o mínimo (juros) e o total.
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
              Todo valor acima dos juros abate direto no principal, reduzindo os juros dos meses
              seguintes.
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
              Sem multa por quitação antecipada.
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-slate-200 text-center text-xs text-slate-500 font-medium">
            Proposta gerada por {data.empresa}. Este documento é uma simulação e não substitui
            contrato.
          </div>
        </div>
      </div>

      <style>{`@media print { body { background: white !important; } @page { size: A4; margin: 15mm; } }`}</style>
    </div>
  );
}
